import { createSelector } from '@reduxjs/toolkit';
import emboldLogo from 'assets/embold-icon.png';
import emboldLoginLogo from 'assets/Embold_login.png';
import { AUTO_LOGIN } from 'utils/constants';
import { isEmbedded } from 'utils/utils';
import { CONFIG_SLICE_NAME } from '../slicesNames';
import * as selectNetworks from './selectNetworks';
import { networkConfigKeys } from './configConstants';

/** @returns {string} The EMBOLD_CLIENT value */
export const name = (state) => state[CONFIG_SLICE_NAME].values.EMBOLD_CLIENT;

/** @returns {string} The EMBOLD_CLIENT_SLUG value */
export const slug = (state) => state[CONFIG_SLICE_NAME].values.EMBOLD_CLIENT_SLUG;

/** @returns {string} The user facing name of the embold client */
export const displayName = (state) => state[CONFIG_SLICE_NAME].values.DISPLAY_NAME;

/** The type of login form used on the login page for the client
 * @returns {PASSWORD|DROPDOWN|DROPDOWN_AUTOCOMPLETE|CHECKMARK} The FORM_VARIANT value
 */
export const loginFormVariant = createSelector(
  [(state) => state[CONFIG_SLICE_NAME].values.FORM_VARIANT],
  (formVariant) => {
    if (isEmbedded()) {
      return AUTO_LOGIN;
    }
    return formVariant;
  }
);

export const loginNetworkName = (state) => state[CONFIG_SLICE_NAME].values.LOGIN_NETWORK_NAME;

/** This constructs a Regex object from the LOGIN_PASSWORD_AUTH_REGEX array.
 * @returns {RegExp} A Regex object
 */
export const loginPasswordRegex = createSelector(
  [(state) => state[CONFIG_SLICE_NAME].values.PASSWORD_REGEX || []],
  (regexArray) => new RegExp(...regexArray)
);

/* ****************************************************** */
// Client level selectors that have Network level overrides
/* ****************************************************** */

/** The image shown in the app header.
 * This returns a network level HEADER_IMAGE if available,
 * or a client level HEADER_IMAGE (either from the local config OR fusion),
 * or lastly the emboldLogo stored locally
 * @returns {string} URI string - The HEADER_IMAGE value for the selected network or the HEADER_IMAGE value on the client level
 */
export const headerImage = createSelector(
  [
    selectNetworks.getNetworkConfigValue(networkConfigKeys.HEADER_IMAGE),
    (state) => state[CONFIG_SLICE_NAME].values.HEADER_IMAGE,
  ],
  (networkLevelImage, clientLevelImage) => networkLevelImage || clientLevelImage || emboldLogo
);

/** The image shown on the login page.
 * This returns a network level LOGIN_IMAGE if available,
 * or a client level LOGIN_IMAGE if available,
 * or lastly the emboldLoginLogo
 * @returns {string} URI string - The login image of the network level, or client level
 */
export const loginImage = createSelector(
  [
    selectNetworks.getNetworkConfigValue(networkConfigKeys.LOGIN_IMAGE),
    (state) => state[CONFIG_SLICE_NAME].values.LOGIN_IMAGE, // from fusion client config
  ],
  (networkLevelImage, fusionImage) => networkLevelImage || fusionImage || emboldLoginLogo
);
